import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { Button, Card, ConfirmDialog, EmptyState, Skeleton, useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { Select } from '../../../shared/ui/Select.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { useUnreadNotificationEntities } from '../../notification/model/notification.queries.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import {
  supplyKeys,
  useDeleteSupply,
  useSendSupply,
  useSupplyReports,
} from '../model/supply.queries.js';
import { SupplyCard } from './SupplyCard.js';
import { SupplyUploadDialog } from './SupplyUploadDialog.js';

/**
 * MAHSULOT TUSHUMI — 1C moddiy hisobotlari.
 *
 * Bitta ekranda ikki ish, chunki ular bitta hujjatning ikki tomoni:
 * buxgalter yuklaydi, skladchi joylashtiradi. Ikki alohida bo'lim qilinsa,
 * "men yuborgan hujjat qayerda?" degan savolga javob topish uchun bo'limlar
 * orasida yurish kerak bo'lardi.
 *
 * Tugmalar RUXSATGA qarab chiqadi: buxgalterda "Yuklash", skladchida
 * "Joylashtirish". Ikkalasi bo'lgan odam (superadmin) ikkalasini ham
 * ko'radi.
 */
export interface SupplyPageProps {
  /**
   * Hujjatni ochish. Sahifa marshrutlashni BILMAYDI — havolani karkas
   * beradi, xuddi `ReceiptPage.onDone` kabi.
   */
  onOpen?: (reportId: string) => void;
}

export const SupplyPage = ({ onOpen }: SupplyPageProps = {}) => {
  const { canAnywhere } = usePermission();
  const queryClient = useQueryClient();
  const toast = useToast();

  const canUpload = canAnywhere('stock.supply.upload');
  const canPlace = canAnywhere('stock.supply.place');

  const [warehouseId, setWarehouseId] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  /* O'chirish SO'RALADI: qoralama izsiz yo'q bo'ladi va uni qaytarib
     bo'lmaydi. Tasdiqda hujjat raqami turadi — noto'g'risi bosilmasin. */
  const [deleting, setDeleting] = useState<{ id: string; docNo: string } | null>(null);

  const sendReport = useSendSupply();
  const deleteReport = useDeleteSupply();

  const fail = (error: unknown, fallback: string): void => {
    toast.show(error instanceof ApiClientError ? error.message : fallback, 'danger');
  };

  /* Qaysi hujjat O'QILMAGAN — yon paneldagi son bilan bir manbadan.
     Ruxsati yo'q xodim uchun so'rov ketmaydi. */
  const unreadIds = useUnreadNotificationEntities('SUPPLY_ARRIVED', 'SupplyReport', canPlace);

  const warehouses = useWarehouses({ limit: 200 });
  const warehouseItems = useMemo(() => warehouses.data?.items ?? [], [warehouses.data]);

  const reports = useSupplyReports({
    ...(warehouseId === '' ? {} : { warehouseId }),
    limit: 50,
  });

  const warehouseName = (id: string): string =>
    warehouseItems.find((w) => w.id === id)?.name ?? `#${id}`;

  const items = reports.data?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('supply.title')}
        description={t('supply.subtitle')}
        filters={
          <Select
            variant="filter"
            label={t('supply.filterWarehouse')}
            placeholder={t('supply.filterAll')}
            value={warehouseId}
            onChange={(event) => setWarehouseId(event.target.value)}
            options={warehouseItems.map((w) => ({ value: w.id, label: `${w.code} — ${w.name}` }))}
          />
        }
        actions={
          canUpload ? (
            <Button onClick={() => setUploadOpen(true)}>
              <Upload size={16} aria-hidden />
              {t('supply.upload')}
            </Button>
          ) : undefined
        }
      />

      {reports.isPending ? (
        <Skeleton lines={6} className="w-full" />
      ) : reports.isError ? (
        <Card>
          <EmptyState
            title={t('supply.loadError')}
            description={
              reports.error instanceof ApiClientError ? reports.error.message : undefined
            }
            action={
              <Button variant="secondary" onClick={() => void reports.refetch()}>
                {t('supply.retry')}
              </Button>
            }
          />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileSpreadsheet size={20} aria-hidden />}
            title={t('supply.empty')}
            description={t(canUpload ? 'supply.emptyHintUpload' : 'supply.emptyHint')}
          />
        </Card>
      ) : (
        /* Kartochkalar — jadval emas. Ro'yxatning savoli "ustunlarni
           solishtirish" emas, "qaysi hujjatda ish qolgan". */
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((report) => (
            <li key={report.id}>
              <SupplyCard
                report={report}
                warehouseName={warehouseName(report.warehouseId)}
                unread={unreadIds.has(report.id)}
                onOpen={() => onOpen?.(report.id)}
                busy={sendReport.isPending || deleteReport.isPending}
                {...(canUpload
                  ? {
                      onSend: () =>
                        sendReport.mutate(report.id, {
                          onSuccess: () =>
                            toast.show(t('supply.sent', { docNo: report.docNo }), 'success'),
                          onError: (error) => fail(error, t('supply.sendFailed')),
                        }),
                      onDelete: () => setDeleting({ id: report.id, docNo: report.docNo }),
                    }
                  : {})}
              />
            </li>
          ))}
        </ul>
      )}

      {canUpload ? (
        <SupplyUploadDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          warehouses={warehouseItems.map((w) => ({ id: w.id, name: w.name, code: w.code }))}
          onUploaded={() => void queryClient.invalidateQueries({ queryKey: supplyKeys.all })}
        />
      ) : null}

      {/* O'CHIRISH TASDIG'I. Qoralama izsiz yo'q bo'ladi, shuning uchun
          oynada hujjat raqami turadi — noto'g'risi bosilmasin. */}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          const target = deleting;
          if (target === null) return;
          deleteReport.mutate(target.id, {
            onSuccess: () => {
              setDeleting(null);
              toast.show(t('supply.deleted', { docNo: target.docNo }), 'success');
            },
            onError: (error) => {
              setDeleting(null);
              fail(error, t('supply.deleteFailed'));
            },
          });
        }}
        title={t('supply.deleteTitle')}
        description={t('supply.deleteConfirm', { docNo: deleting?.docNo ?? '' })}
        confirmLabel={t('supply.delete')}
        cancelLabel={t('supply.cancel')}
        loading={deleteReport.isPending}
      />
    </div>
  );
};
